import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Calendar, BookOpen, User, CheckCircle, Clock, XCircle, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const statusConfig = {
    'New': { color: 'bg-blue-100 text-blue-700', icon: FileText },
    'Under Review': { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
    'Accepted': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
    'Rejected': { color: 'bg-red-100 text-red-700', icon: XCircle },
  };

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/applications/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setApplication(data);
        } else {
          navigate('/admin/applications');
        }
      } catch (error) {
        console.error('Failed to fetch application:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplication();
  }, [id, token, navigate]);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/applications/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setApplication({ ...application, status: newStatus });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!application) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-[#475569]">Application not found</p>
          <Link to="/admin/applications">
            <Button className="mt-4">Back to Applications</Button>
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const StatusIcon = statusConfig[application.status]?.icon || FileText;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Link
              to="/admin/applications"
              className="inline-flex items-center text-[#475569] hover:text-[#1e3a5f] mb-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Applications
            </Link>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-[#1e3a5f]">
              {application.applicationNumber}
            </h1>
            <p className="text-[#475569]">
              Submitted on {new Date(application.submittedAt).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <Badge className={`${statusConfig[application.status]?.color} text-sm px-4 py-2`}>
              <StatusIcon className="w-4 h-4 mr-2" />
              {application.status}
            </Badge>
          </div>
        </div>

        {/* Status Update */}
        <Card className="border-none shadow-md">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <span className="text-[#475569] font-medium">Update Status:</span>
              <Select
                value={application.status}
                onValueChange={handleStatusChange}
                disabled={updating}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="Under Review">Under Review</SelectItem>
                  <SelectItem value="Accepted">Accepted</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              {updating && (
                <span className="text-[#475569] text-sm">Updating...</span>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Personal Information */}
          <Card className="lg:col-span-2 border-none shadow-md">
            <CardHeader>
              <CardTitle className="text-[#1e3a5f] flex items-center gap-2">
                <User className="w-5 h-5 text-[#d4a855]" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[#475569] text-sm">Full Name</label>
                  <p className="text-[#1e3a5f] font-medium">{application.fullName}</p>
                </div>
                <div>
                  <label className="text-[#475569] text-sm">Gender</label>
                  <p className="text-[#1e3a5f] font-medium capitalize">{application.gender}</p>
                </div>
                <div>
                  <label className="text-[#475569] text-sm">Date of Birth</label>
                  <p className="text-[#1e3a5f] font-medium">{application.dateOfBirth}</p>
                </div>
                <div>
                  <label className="text-[#475569] text-sm flex items-center gap-1">
                    <Mail className="w-4 h-4" /> Email
                  </label>
                  <p className="text-[#1e3a5f] font-medium">{application.email}</p>
                </div>
                <div>
                  <label className="text-[#475569] text-sm flex items-center gap-1">
                    <Phone className="w-4 h-4" /> Phone
                  </label>
                  <p className="text-[#1e3a5f] font-medium">{application.phoneNumber}</p>
                </div>
                <div>
                  <label className="text-[#475569] text-sm flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> Location
                  </label>
                  <p className="text-[#1e3a5f] font-medium">
                    {[application.ward, application.district, application.region].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Programme Information */}
          <Card className="border-none shadow-md">
            <CardHeader>
              <CardTitle className="text-[#1e3a5f] flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#d4a855]" />
                Programme
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-[#475569] text-sm">Department</label>
                <p className="text-[#1e3a5f] font-medium">{application.departmentName}</p>
              </div>
              <div>
                <label className="text-[#475569] text-sm">Programme</label>
                <p className="text-[#1e3a5f] font-medium">{application.programmeName}</p>
              </div>
              <div>
                <label className="text-[#475569] text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> Intake
                </label>
                <Badge className="bg-[#d4a855] text-[#1e3a5f] mt-1">
                  {application.intake} Intake
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Education Background */}
        <Card className="border-none shadow-md">
          <CardHeader>
            <CardTitle className="text-[#1e3a5f]">Education Background</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-[#f8fafc] rounded-lg p-4">
              <p className="text-[#475569] whitespace-pre-wrap">{application.educationBackground}</p>
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card className="border-none shadow-md">
          <CardHeader>
            <CardTitle className="text-[#1e3a5f]">Application Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#1e3a5f] rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-[#1e3a5f] font-medium">Application Submitted</p>
                  <p className="text-[#475569] text-sm">
                    {new Date(application.submittedAt).toLocaleString()}
                  </p>
                </div>
              </div>
              {application.updatedAt !== application.submittedAt && (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#d4a855] rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#1e3a5f]" />
                  </div>
                  <div>
                    <p className="text-[#1e3a5f] font-medium">Last Updated</p>
                    <p className="text-[#475569] text-sm">
                      {new Date(application.updatedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminApplicationDetail;
